import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import View from '../common/View';
import Text from '../common/Text';
import Button from '../common/Button';
import './ForgotPassword.css';

const ForgotPassword: React.FC = () => {
  const { t } = useLanguage();
  const { onRequestPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const lang = navigator.language || 'en-US';
      await onRequestPasswordReset(email, lang);
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View className="forgot-password">
      <View className="forgot-password-container">
        <View className="forgot-password-header">
          <Text variant="h1">{t('FORGOT_PASSWORD') || 'Forgot password'}</Text>
          <Text variant="body">
            {sent
              ? t('RESET_LINK_SENT') ||
                'If an account exists for that email, a reset link has been sent.'
              : t('FORGOT_PASSWORD_SUBTITLE') ||
                'Enter your email and we’ll send you a reset link.'}
          </Text>
        </View>

        {!sent && (
          <form onSubmit={handleSubmit} className="forgot-password-form">
            <View className="form-group">
              <Text variant="label">{t('EMAIL') || 'Email'}</Text>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="form-input"
                placeholder="you@example.com"
                autoComplete="email"
                required
                disabled={loading}
              />
            </View>

            {error && (
              <View className="signin-error">
                <Text variant="body">{error}</Text>
              </View>
            )}

            <Button
              type="submit"
              variant="primary"
              size="large"
              className="reset-button"
              disabled={loading}
            >
              {loading
                ? t('SENDING') || 'Sending…'
                : t('SEND_RESET_LINK') || 'Send reset link'}
            </Button>
          </form>
        )}

        <View className="forgot-password-footer">
          <Text variant="body">
            <Link to="/signin">
              {t('BACK_TO_SIGN_IN') || 'Back to sign in'}
            </Link>
          </Text>
        </View>
      </View>
    </View>
  );
};

export default ForgotPassword;
